import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Car,
  Download,
  Eye,
  FileText,
  Gauge,
  Loader,
  Plus,
  RefreshCw,
  Upload,
  User,
  Wrench,
  X,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, FadeIn } from '../../components/ui';
import { downloadDocument, previewDocument } from '../../utils/documentViewer';

const API = '/erp/api/v1';

async function getToken() {
  const token = localStorage.getItem('nexus_token');
  if (!token) {
    window.location.assign('/erp/login');
    return '';
  }
  return token;
}

async function authHeaders() {
  const token = await getToken();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function authOnlyHeaders() {
  const token = await getToken();
  return { Authorization: `Bearer ${token}` };
}

const today = new Date().toISOString().split('T')[0];
const month = new Date().toISOString().slice(0, 7);

const openDocumentosOCR = (tipo = 'documento_veiculo') => {
  const params = new URLSearchParams({ acao: 'importar', modulo: 'frota', tipo });
  window.location.assign(`/erp/documentos?${params.toString()}`);
};

const emptyVeiculo = {
  placa: '',
  marca: '',
  modelo: '',
  ano_fabricacao: '',
  ano_modelo: '',
  cor: '',
  tipo: 'carro',
  status: 'ativo',
  renavam: '',
  chassi: '',
  km_atual: '',
  data_vencimento_ipva: '',
  data_vencimento_licenciamento: '',
};

const emptyDoc = {
  tipo: 'crlv',
  ano_referencia: new Date().getFullYear().toString(),
  competencia: month,
  data_documento: today,
  observacao: '',
  file: null,
};

const emptyDespesa = {
  tipo: 'manutencao',
  data: today,
  fornecedor: '',
  descricao: '',
  valor: '',
  km_atual: '',
  km_proxima: '',
  observacao: '',
  file: null,
};

const emptyUso = {
  motorista_nome: '',
  data: today,
  hora_saida: '',
  hora_retorno: '',
  km_saida: '',
  km_retorno: '',
  destino: '',
  finalidade: '',
  defeito_visual: '',
  defeito_uso: '',
  observacao: '',
};

const emptyPneu = {
  codigo: '',
  posicao: '',
  marca: '',
  modelo: '',
  data_instalacao: today,
  km_instalacao: '',
  observacao: '',
};

const emptyMulta = {
  auto_infracao: '',
  data_infracao: today,
  hora_infracao: '',
  local: '',
  descricao: '',
  valor: '',
  pontos: '',
  vencimento: '',
  motorista_indicado: '',
  observacao: '',
  file: null,
};

export const Frota = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('veiculos');
  const [veiculos, setVeiculos] = useState([]);
  const [dossie, setDossie] = useState(null);
  const [selectedVeiculoId, setSelectedVeiculoId] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState('');
  const [error, setError] = useState('');

  const [veiculoForm, setVeiculoForm] = useState(emptyVeiculo);
  const [docForm, setDocForm] = useState(emptyDoc);
  const [despesaForm, setDespesaForm] = useState(emptyDespesa);
  const [usoForm, setUsoForm] = useState(emptyUso);
  const [pneuForm, setPneuForm] = useState(emptyPneu);
  const [multaForm, setMultaForm] = useState(emptyMulta);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      const [vRes, dRes] = await Promise.all([
        fetch(`${API}/frota/veiculos?per_page=200`, { headers }),
        fetch(`${API}/frota/dashboard`, { headers }),
      ]);
      if (vRes.ok) {
        const data = await vRes.json();
        const items = data.items || [];
        setVeiculos(items);
        if (!selectedVeiculoId && items.length > 0) setSelectedVeiculoId(items[0].id);
      }
      if (dRes.ok) setDashboard(await dRes.json());
    } catch (e) {
      setError('Erro ao carregar frota');
    } finally {
      setLoading(false);
    }
  }, [selectedVeiculoId]);

  const loadDossie = useCallback(async (id = selectedVeiculoId) => {
    if (!id) {
      setDossie(null);
      return;
    }
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/frota/veiculos/${id}/dossie`, { headers });
      if (res.ok) setDossie(await res.json());
    } catch (e) {
      setError('Erro ao carregar dossiê do veículo');
    }
  }, [selectedVeiculoId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadDossie(); }, [loadDossie]);

  useEffect(() => {
    const action = searchParams.get('acao');
    if (!action) return;
    const placa = (searchParams.get('placa') || '').toUpperCase();
    const veiculo = placa ? veiculos.find((item) => (item.placa || '').replace('-', '').toUpperCase() === placa.replace('-', '')) : null;
    if (veiculo) setSelectedVeiculoId(veiculo.id);
    const despesaPrefill = {
      ...emptyDespesa,
      data: searchParams.get('data') || emptyDespesa.data,
      fornecedor: searchParams.get('fornecedor') || '',
      valor: searchParams.get('valor') || '',
      tipo: searchParams.get('tipo') || emptyDespesa.tipo,
      descricao: searchParams.get('observacao') || '',
      observacao: searchParams.get('observacao') || '',
    };
    const multaPrefill = {
      ...emptyMulta,
      data_infracao: searchParams.get('data') || emptyMulta.data_infracao,
      motorista_indicado: searchParams.get('motorista') || '',
      valor: searchParams.get('valor') || '',
      descricao: searchParams.get('observacao') || '',
    };
    const handlers = {
      veiculo: () => { setActiveTab('veiculos'); setVeiculoForm(emptyVeiculo); setModal('veiculo'); },
      documento: () => { setActiveTab('dossie'); setDocForm(emptyDoc); setModal('documento'); },
      despesa: () => { setActiveTab('despesas'); setDespesaForm(despesaPrefill); setModal('despesa'); },
      manutencao: () => { setActiveTab('despesas'); setDespesaForm({ ...despesaPrefill, tipo: 'manutencao' }); setModal('despesa'); },
      uso: () => { setActiveTab('uso'); setUsoForm(emptyUso); setModal('uso'); },
      pneu: () => { setActiveTab('pneus'); setPneuForm(emptyPneu); setModal('pneu'); },
      multa: () => { setActiveTab('multas'); setMultaForm(multaPrefill); setModal('multa'); },
    };
    if (handlers[action]) handlers[action]();
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, veiculos]);

  const fmtBRL = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  const fmtDate = (s) => s ? new Date(`${s}T12:00:00`).toLocaleDateString('pt-BR') : '-';
  const selectedVeiculo = veiculos.find(v => v.id === selectedVeiculoId);

  const closeModal = () => {
    setModal('');
    setError('');
  };

  const afterSave = async () => {
    closeModal();
    await loadData();
    await loadDossie();
  };

  const saveVeiculo = async () => {
    if (!veiculoForm.placa || !veiculoForm.marca || !veiculoForm.modelo) {
      setError('Placa, marca e modelo são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      const headers = await authHeaders();
      const body = {
        ...veiculoForm,
        ano_fabricacao: veiculoForm.ano_fabricacao ? parseInt(veiculoForm.ano_fabricacao, 10) : null,
        ano_modelo: veiculoForm.ano_modelo ? parseInt(veiculoForm.ano_modelo, 10) : null,
        km_atual: veiculoForm.km_atual ? parseInt(veiculoForm.km_atual, 10) : 0,
      };
      const res = await fetch(`${API}/frota/veiculos`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setSelectedVeiculoId(saved.id);
      setVeiculoForm(emptyVeiculo);
      await afterSave();
    } catch (e) {
      setError('Erro ao salvar veículo');
    } finally {
      setSaving(false);
    }
  };

  const saveDocumento = async () => {
    if (!selectedVeiculoId || !docForm.file) {
      setError('Arquivo é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const headers = await authOnlyHeaders();
      const fd = new FormData();
      Object.entries(docForm).forEach(([k, v]) => {
        if (k === 'file') fd.append('file', v);
        else fd.append(k, v || '');
      });
      const res = await fetch(`${API}/frota/veiculos/${selectedVeiculoId}/documentos`, { method: 'POST', headers, body: fd });
      if (!res.ok) throw new Error();
      setDocForm(emptyDoc);
      await afterSave();
    } catch (e) {
      setError('Erro ao anexar documento');
    } finally {
      setSaving(false);
    }
  };

  const vehicleDocUrl = (id) => `${API}/frota/veiculos/documentos/${id}/download`;
  const docName = (doc) => doc?.nome_armazenado || doc?.nome_original || 'documento-veiculo';

  const previewDoc = async (doc) => {
    try {
      const headers = await authOnlyHeaders();
      await previewDocument(vehicleDocUrl(doc.id), docName(doc), headers);
    } catch (err) {
      setError(err.message || 'Erro ao abrir documento');
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const headers = await authOnlyHeaders();
      await downloadDocument(vehicleDocUrl(doc.id), docName(doc), headers);
    } catch (err) {
      setError(err.message || 'Erro ao baixar documento');
    }
  };

  const saveDespesa = async () => {
    if (!selectedVeiculoId || !despesaForm.tipo) {
      setError('Tipo é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const headers = despesaForm.file ? await authOnlyHeaders() : await authHeaders();
      let body;
      if (despesaForm.file) {
        body = new FormData();
        Object.entries(despesaForm).forEach(([k, v]) => {
          if (k === 'file') body.append('file', v);
          else body.append(k, v || '');
        });
      } else {
        body = JSON.stringify({
          ...despesaForm,
          valor: despesaForm.valor ? parseFloat(despesaForm.valor) : 0,
          km_atual: despesaForm.km_atual ? parseInt(despesaForm.km_atual, 10) : null,
          km_proxima: despesaForm.km_proxima ? parseInt(despesaForm.km_proxima, 10) : null,
        });
      }
      const res = await fetch(`${API}/frota/veiculos/${selectedVeiculoId}/despesas`, { method: 'POST', headers, body });
      if (!res.ok) throw new Error();
      setDespesaForm(emptyDespesa);
      await afterSave();
    } catch (e) {
      setError('Erro ao salvar despesa');
    } finally {
      setSaving(false);
    }
  };

  const saveUso = async () => {
    if (!selectedVeiculoId || !usoForm.data) {
      setError('Data é obrigatória');
      return;
    }
    setSaving(true);
    try {
      const headers = await authHeaders();
      const body = {
        ...usoForm,
        km_saida: usoForm.km_saida ? parseInt(usoForm.km_saida, 10) : null,
        km_retorno: usoForm.km_retorno ? parseInt(usoForm.km_retorno, 10) : null,
      };
      const res = await fetch(`${API}/frota/veiculos/${selectedVeiculoId}/usos`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      setUsoForm(emptyUso);
      await afterSave();
    } catch (e) {
      setError('Erro ao salvar uso');
    } finally {
      setSaving(false);
    }
  };

  const savePneu = async () => {
    if (!selectedVeiculoId || !pneuForm.codigo) {
      setError('Código do pneu é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const headers = await authHeaders();
      const body = {
        ...pneuForm,
        km_instalacao: pneuForm.km_instalacao ? parseInt(pneuForm.km_instalacao, 10) : null,
      };
      const res = await fetch(`${API}/frota/veiculos/${selectedVeiculoId}/pneus`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      setPneuForm(emptyPneu);
      await afterSave();
    } catch (e) {
      setError('Erro ao salvar pneu');
    } finally {
      setSaving(false);
    }
  };

  const saveMulta = async () => {
    if (!selectedVeiculoId || !multaForm.data_infracao) {
      setError('Data da infração é obrigatória');
      return;
    }
    setSaving(true);
    try {
      const headers = multaForm.file ? await authOnlyHeaders() : await authHeaders();
      let body;
      if (multaForm.file) {
        body = new FormData();
        Object.entries(multaForm).forEach(([k, v]) => {
          if (k === 'file') body.append('file', v);
          else body.append(k, v || '');
        });
      } else {
        body = JSON.stringify({
          ...multaForm,
          valor: multaForm.valor ? parseFloat(multaForm.valor) : 0,
          pontos: multaForm.pontos ? parseInt(multaForm.pontos, 10) : null,
        });
      }
      const res = await fetch(`${API}/frota/veiculos/${selectedVeiculoId}/multas`, { method: 'POST', headers, body });
      if (!res.ok) throw new Error();
      setMultaForm(emptyMulta);
      await afterSave();
    } catch (e) {
      setError('Erro ao salvar multa');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'veiculos', label: 'Veículos' },
    { id: 'dossie', label: 'Dossiê' },
    { id: 'despesas', label: 'Despesas' },
    { id: 'uso', label: 'Uso' },
    { id: 'pneus', label: 'Pneus' },
    { id: 'multas', label: 'Multas' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Frota</h1>
          <p className="text-white/50">Veículos, documentos, uso, despesas e manutenção</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <a
            href={`${process.env.PUBLIC_URL || ''}/manuals/playbook-humano-frota.md`}
            download
            className="relative inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-accent-primary/50 bg-white/10 text-white/90 border border-white/20 hover:bg-white/20 hover:border-white/30 px-5 py-2.5 text-sm"
          >
            <Download className="w-4 h-4" /> Manual
          </a>
          <button onClick={loadData} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => openDocumentosOCR('multa')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm flex items-center gap-2">
            <Upload size={16} /> Multa
          </button>
          <button onClick={() => openDocumentosOCR('manutencao')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm flex items-center gap-2">
            <Upload size={16} /> Manutenção
          </button>
          <button onClick={() => openDocumentosOCR('documento_veiculo')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm flex items-center gap-2">
            <Upload size={16} /> Documento
          </button>
          <GlassButton onClick={() => setModal('veiculo')} className="flex items-center gap-2">
            <Plus size={16} /> Veículo
          </GlassButton>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GlassCard className="p-4"><div className="flex items-center gap-3"><Car className="text-blue-300" size={20} /><div><p className="text-white/50 text-xs">Veículos</p><p className="text-2xl font-bold text-white">{dashboard?.total_veiculos ?? veiculos.length}</p></div></div></GlassCard>
        <GlassCard className="p-4"><div className="flex items-center gap-3"><Gauge className="text-green-300" size={20} /><div><p className="text-white/50 text-xs">Ativos</p><p className="text-2xl font-bold text-white">{dashboard?.ativos ?? 0}</p></div></div></GlassCard>
        <GlassCard className="p-4"><div className="flex items-center gap-3"><Wrench className="text-yellow-300" size={20} /><div><p className="text-white/50 text-xs">Manutenção</p><p className="text-2xl font-bold text-white">{dashboard?.em_manutencao ?? 0}</p></div></div></GlassCard>
        <GlassCard className="p-4"><div className="flex items-center gap-3"><AlertTriangle className="text-red-300" size={20} /><div><p className="text-white/50 text-xs">Multas abertas</p><p className="text-2xl font-bold text-white">{dossie?.alertas?.multas_abertas ?? 0}</p></div></div></GlassCard>
      </div>

      <GlassCard className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Car size={18} className="text-blue-300" />
          <select value={selectedVeiculoId} onChange={e => setSelectedVeiculoId(e.target.value)} className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm min-w-[260px]">
            <option value="">Selecionar veículo...</option>
            {veiculos.map(v => <option key={v.id} value={v.id}>{v.placa} · {v.marca} {v.modelo}</option>)}
          </select>
          {selectedVeiculo && <span className="text-white/50 text-sm">KM {Number(selectedVeiculo.km_atual || 0).toLocaleString('pt-BR')}</span>}
          {selectedVeiculoId && (
            <div className="flex flex-wrap gap-2 ml-auto">
              <button onClick={() => setModal('documento')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2"><Upload size={14} /> Documento</button>
              <button onClick={() => setModal('despesa')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2"><FileText size={14} /> Despesa</button>
              <button onClick={() => setModal('uso')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2"><User size={14} /> Uso</button>
              <button onClick={() => setModal('pneu')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2"><Gauge size={14} /> Pneu</button>
              <button onClick={() => setModal('multa')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2"><AlertTriangle size={14} /> Multa</button>
            </div>
          )}
        </div>
      </GlassCard>

      <div className="flex gap-2 border-b border-white/10 overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 text-sm rounded-t-lg whitespace-nowrap ${activeTab === tab.id ? 'bg-white/10 text-white border-b-2 border-blue-400' : 'text-white/50 hover:text-white/70'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader className="animate-spin text-white/50" /></div>
      ) : (
        <FadeIn>
          {activeTab === 'veiculos' && (
            <GlassCard className="overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-white/10">{['Placa', 'Modelo', 'Ano', 'Status', 'KM', 'Vencimentos'].map(h => <th key={h} className="text-left text-white/50 text-xs px-4 py-3 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {veiculos.map(v => (
                    <tr key={v.id} onClick={() => { setSelectedVeiculoId(v.id); setActiveTab('dossie'); }} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
                      <td className="px-4 py-3 text-white text-sm">{v.placa}</td>
                      <td className="px-4 py-3 text-white/70 text-sm">{v.marca} {v.modelo}</td>
                      <td className="px-4 py-3 text-white/60 text-sm">{v.ano || '-'}</td>
                      <td className="px-4 py-3 text-white/60 text-sm">{v.status}</td>
                      <td className="px-4 py-3 text-white/60 text-sm">{Number(v.km_atual || 0).toLocaleString('pt-BR')}</td>
                      <td className="px-4 py-3 text-white/40 text-xs">IPVA/licenciamento no dossiê</td>
                    </tr>
                  ))}
                  {veiculos.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-white/30">Nenhum veículo cadastrado</td></tr>}
                </tbody>
              </table>
            </GlassCard>
          )}

          {activeTab === 'dossie' && dossie && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <GlassCard className="p-5">
                <p className="text-white/50 text-sm mb-3">Veículo</p>
                <h3 className="text-white font-semibold text-lg">{dossie.veiculo.placa}</h3>
                <p className="text-white/60 text-sm">{dossie.veiculo.marca} {dossie.veiculo.modelo}</p>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-white/40">KM</span><span className="text-white/80">{Number(dossie.veiculo.km_atual || 0).toLocaleString('pt-BR')}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Status</span><span className="text-white/80">{dossie.veiculo.status}</span></div>
                  <div className="flex justify-between"><span className="text-white/40">RENAVAM</span><span className="text-white/80">{dossie.veiculo.renavam || '-'}</span></div>
                </div>
              </GlassCard>
              <GlassCard className="p-5">
                <p className="text-white/50 text-sm mb-3">Alertas</p>
                <p className="text-2xl font-bold text-white">{dossie.alertas.multas_abertas} multa(s)</p>
                <p className="text-white/40 text-xs mt-2">{dossie.alertas.documentos} documento(s) anexado(s)</p>
              </GlassCard>
              <GlassCard className="p-5">
                <p className="text-white/50 text-sm mb-3">Pneus em uso</p>
                <p className="text-2xl font-bold text-white">{dossie.alertas.pneus_em_uso}</p>
                <p className="text-white/40 text-xs mt-2">KM calculado pelo odômetro atual</p>
              </GlassCard>
              <GlassCard className="p-5 lg:col-span-3">
                <p className="text-white/50 text-sm mb-3">Documentos</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
	                  {(dossie.documentos || []).slice(0, 8).map(doc => (
	                    <div key={doc.id} className="border border-white/10 rounded-lg p-3">
	                      <div className="flex items-start justify-between gap-3">
	                        <div>
	                          <p className="text-white/80 text-sm">{doc.nome_original}</p>
	                          <p className="text-white/40 text-xs">{doc.tipo} · {doc.ano_referencia || doc.competencia || '-'} · {fmtDate(doc.data_documento)}</p>
	                        </div>
	                        <div className="flex items-center gap-1">
	                          <button title="Abrir" onClick={() => previewDoc(doc)} className="rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white">
	                            <Eye className="h-4 w-4" />
	                          </button>
	                          <button title="Baixar" onClick={() => downloadDoc(doc)} className="rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white">
	                            <Download className="h-4 w-4" />
	                          </button>
	                        </div>
	                      </div>
	                    </div>
	                  ))}
                  {(dossie.documentos || []).length === 0 && <p className="text-white/35 text-sm">Sem documentos anexados</p>}
                </div>
              </GlassCard>
            </div>
          )}

          {activeTab === 'despesas' && <ListTable rows={dossie?.despesas || []} cols={['data', 'tipo', 'fornecedor', 'valor', 'km_atual']} fmtDate={fmtDate} fmtBRL={fmtBRL} />}
          {activeTab === 'uso' && <ListTable rows={dossie?.usos || []} cols={['data', 'motorista', 'destino', 'km_rodado', 'defeito_visual']} fmtDate={fmtDate} fmtBRL={fmtBRL} />}
          {activeTab === 'pneus' && <ListTable rows={dossie?.pneus || []} cols={['codigo', 'posicao', 'marca', 'km_instalacao', 'km_rodado', 'status']} fmtDate={fmtDate} fmtBRL={fmtBRL} />}
          {activeTab === 'multas' && <ListTable rows={dossie?.multas || []} cols={['data_infracao', 'auto_infracao', 'motorista_indicado', 'valor', 'status']} fmtDate={fmtDate} fmtBRL={fmtBRL} />}
        </FadeIn>
      )}

      {modal && (
        <FrotaModal title={{
          veiculo: 'Novo Veículo',
          documento: 'Anexar Documento',
          despesa: 'Registrar Despesa',
          uso: 'Registrar Uso',
          pneu: 'Registrar Pneu',
          multa: 'Registrar Multa',
        }[modal]} onClose={closeModal} error={error} saving={saving} onSave={{
          veiculo: saveVeiculo,
          documento: saveDocumento,
          despesa: saveDespesa,
          uso: saveUso,
          pneu: savePneu,
          multa: saveMulta,
        }[modal]}>
          {modal === 'veiculo' && <VeiculoForm form={veiculoForm} setForm={setVeiculoForm} />}
          {modal === 'documento' && <DocumentoForm form={docForm} setForm={setDocForm} />}
          {modal === 'despesa' && <DespesaForm form={despesaForm} setForm={setDespesaForm} />}
          {modal === 'uso' && <UsoForm form={usoForm} setForm={setUsoForm} />}
          {modal === 'pneu' && <PneuForm form={pneuForm} setForm={setPneuForm} selectedVeiculo={selectedVeiculo} />}
          {modal === 'multa' && <MultaForm form={multaForm} setForm={setMultaForm} />}
        </FrotaModal>
      )}
    </div>
  );
};

const Field = ({ label, children }) => <div><label className="text-white/50 text-xs mb-1 block">{label}</label>{children}</div>;

const Select = ({ value, onChange, children }) => (
  <select value={value} onChange={onChange} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">{children}</select>
);

const Input = (props) => <input {...props} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30" />;
const TextArea = (props) => <textarea {...props} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none placeholder-white/30" />;

const FrotaModal = ({ title, children, onClose, onSave, saving, error }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
    <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-white font-semibold text-lg">{title}</h2>
        <button onClick={onClose} className="text-white/40 hover:text-white"><X size={20} /></button>
      </div>
      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
      {children}
      <div className="flex gap-3 mt-6">
        <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 text-sm">Cancelar</button>
        <GlassButton onClick={onSave} disabled={saving} className="flex-1 flex items-center justify-center gap-2">
          {saving ? <Loader size={14} className="animate-spin" /> : null}
          {saving ? 'Salvando...' : 'Salvar'}
        </GlassButton>
      </div>
    </div>
  </div>
);

const VeiculoForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3">
      <Field label="Placa *"><GlassInput value={form.placa} onChange={e => setForm(f => ({ ...f, placa: e.target.value.toUpperCase() }))} placeholder="ABC1D23" /></Field>
      <Field label="KM atual"><GlassInput type="number" value={form.km_atual} onChange={e => setForm(f => ({ ...f, km_atual: e.target.value }))} /></Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Marca *"><GlassInput value={form.marca} onChange={e => setForm(f => ({ ...f, marca: e.target.value }))} /></Field>
      <Field label="Modelo *"><GlassInput value={form.modelo} onChange={e => setForm(f => ({ ...f, modelo: e.target.value }))} /></Field>
    </div>
    <div className="grid grid-cols-4 gap-3">
      <Field label="Ano fab."><Input type="number" value={form.ano_fabricacao} onChange={e => setForm(f => ({ ...f, ano_fabricacao: e.target.value }))} /></Field>
      <Field label="Ano modelo"><Input type="number" value={form.ano_modelo} onChange={e => setForm(f => ({ ...f, ano_modelo: e.target.value }))} /></Field>
      <Field label="Cor"><Input value={form.cor} onChange={e => setForm(f => ({ ...f, cor: e.target.value }))} /></Field>
      <Field label="Tipo"><Input value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} /></Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="RENAVAM"><Input value={form.renavam} onChange={e => setForm(f => ({ ...f, renavam: e.target.value }))} /></Field>
      <Field label="Chassi"><Input value={form.chassi} onChange={e => setForm(f => ({ ...f, chassi: e.target.value }))} /></Field>
    </div>
  </div>
);

const DocumentoForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3">
      <Field label="Tipo"><Select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}><option value="crlv">CRLV/documento anual</option><option value="dossie_detran">Dossiê DETRAN</option><option value="seguro">Seguro</option><option value="multa">Multa</option><option value="manutencao">Manutenção</option><option value="outro">Outro</option></Select></Field>
      <Field label="Ano"><Input type="number" value={form.ano_referencia} onChange={e => setForm(f => ({ ...f, ano_referencia: e.target.value }))} /></Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Data"><Input type="date" value={form.data_documento} onChange={e => setForm(f => ({ ...f, data_documento: e.target.value }))} /></Field>
      <Field label="Arquivo *"><Input type="file" onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] || null }))} /></Field>
    </div>
    <Field label="Observação"><TextArea rows={2} value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} /></Field>
  </div>
);

const DespesaForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-3">
      <Field label="Tipo"><Select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}><option value="manutencao">Manutenção</option><option value="reparo">Reparo</option><option value="oleo">Troca de óleo</option><option value="pneu">Pneu</option><option value="seguro">Seguro</option><option value="multa">Multa</option><option value="combustivel">Combustível</option><option value="outro">Outro</option></Select></Field>
      <Field label="Data"><Input type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} /></Field>
      <Field label="Valor"><Input type="number" step="0.01" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} /></Field>
    </div>
    <Field label="Fornecedor"><GlassInput value={form.fornecedor} onChange={e => setForm(f => ({ ...f, fornecedor: e.target.value }))} /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="KM atual"><Input type="number" value={form.km_atual} onChange={e => setForm(f => ({ ...f, km_atual: e.target.value }))} /></Field>
      <Field label="Próxima manutenção KM"><Input type="number" value={form.km_proxima} onChange={e => setForm(f => ({ ...f, km_proxima: e.target.value }))} /></Field>
    </div>
    <Field label="Arquivo da despesa"><Input type="file" onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] || null }))} /></Field>
    <Field label="Descrição"><TextArea rows={2} value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} /></Field>
  </div>
);

const UsoForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3"><Field label="Motorista"><GlassInput value={form.motorista_nome} onChange={e => setForm(f => ({ ...f, motorista_nome: e.target.value }))} /></Field><Field label="Data"><Input type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} /></Field></div>
    <div className="grid grid-cols-4 gap-3"><Field label="Saída"><Input type="time" value={form.hora_saida} onChange={e => setForm(f => ({ ...f, hora_saida: e.target.value }))} /></Field><Field label="Retorno"><Input type="time" value={form.hora_retorno} onChange={e => setForm(f => ({ ...f, hora_retorno: e.target.value }))} /></Field><Field label="KM saída"><Input type="number" value={form.km_saida} onChange={e => setForm(f => ({ ...f, km_saida: e.target.value }))} /></Field><Field label="KM retorno"><Input type="number" value={form.km_retorno} onChange={e => setForm(f => ({ ...f, km_retorno: e.target.value }))} /></Field></div>
    <Field label="Destino"><GlassInput value={form.destino} onChange={e => setForm(f => ({ ...f, destino: e.target.value }))} /></Field>
    <Field label="Defeito visual"><TextArea rows={2} value={form.defeito_visual} onChange={e => setForm(f => ({ ...f, defeito_visual: e.target.value }))} placeholder="Risco, batida, pneu baixo..." /></Field>
    <Field label="Defeito ao utilizar"><TextArea rows={2} value={form.defeito_uso} onChange={e => setForm(f => ({ ...f, defeito_uso: e.target.value }))} placeholder="Barulho, luz no painel, freio..." /></Field>
  </div>
);

const PneuForm = ({ form, setForm, selectedVeiculo }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3"><Field label="Código *"><GlassInput value={form.codigo} onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))} /></Field><Field label="Posição"><GlassInput value={form.posicao} onChange={e => setForm(f => ({ ...f, posicao: e.target.value }))} placeholder="Dianteiro esquerdo" /></Field></div>
    <div className="grid grid-cols-2 gap-3"><Field label="Marca"><Input value={form.marca} onChange={e => setForm(f => ({ ...f, marca: e.target.value }))} /></Field><Field label="Modelo"><Input value={form.modelo} onChange={e => setForm(f => ({ ...f, modelo: e.target.value }))} /></Field></div>
    <div className="grid grid-cols-2 gap-3"><Field label="Data instalação"><Input type="date" value={form.data_instalacao} onChange={e => setForm(f => ({ ...f, data_instalacao: e.target.value }))} /></Field><Field label="KM instalação"><Input type="number" value={form.km_instalacao || selectedVeiculo?.km_atual || ''} onChange={e => setForm(f => ({ ...f, km_instalacao: e.target.value }))} /></Field></div>
    <Field label="Observação"><TextArea rows={2} value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} /></Field>
  </div>
);

const MultaForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-3"><Field label="Auto"><Input value={form.auto_infracao} onChange={e => setForm(f => ({ ...f, auto_infracao: e.target.value }))} /></Field><Field label="Data"><Input type="date" value={form.data_infracao} onChange={e => setForm(f => ({ ...f, data_infracao: e.target.value }))} /></Field><Field label="Hora"><Input type="time" value={form.hora_infracao} onChange={e => setForm(f => ({ ...f, hora_infracao: e.target.value }))} /></Field></div>
    <Field label="Local"><GlassInput value={form.local} onChange={e => setForm(f => ({ ...f, local: e.target.value }))} /></Field>
    <div className="grid grid-cols-3 gap-3"><Field label="Valor"><Input type="number" step="0.01" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} /></Field><Field label="Pontos"><Input type="number" value={form.pontos} onChange={e => setForm(f => ({ ...f, pontos: e.target.value }))} /></Field><Field label="Vencimento"><Input type="date" value={form.vencimento} onChange={e => setForm(f => ({ ...f, vencimento: e.target.value }))} /></Field></div>
    <Field label="Motorista indicado"><GlassInput value={form.motorista_indicado} onChange={e => setForm(f => ({ ...f, motorista_indicado: e.target.value }))} placeholder="Preencher se souber; senão o sistema tenta pelo uso do dia" /></Field>
    <Field label="PDF/foto da multa"><Input type="file" onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] || null }))} /></Field>
    <Field label="Descrição"><TextArea rows={2} value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} /></Field>
  </div>
);

const ListTable = ({ rows, cols, fmtDate, fmtBRL }) => (
  <GlassCard className="overflow-hidden">
    <table className="w-full">
      <thead><tr className="border-b border-white/10">{cols.map(c => <th key={c} className="text-left text-white/50 text-xs px-4 py-3 font-medium">{c.replaceAll('_', ' ')}</th>)}</tr></thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.id} className="border-b border-white/5">
            {cols.map(c => {
              const value = row[c];
              const shown = c.includes('data') || c === 'vencimento' ? fmtDate(value) : c === 'valor' ? fmtBRL(value) : value || '-';
              return <td key={c} className="px-4 py-3 text-white/70 text-sm">{shown}</td>;
            })}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-white/30">Sem registros</td></tr>}
      </tbody>
    </table>
  </GlassCard>
);

export default Frota;
